import {
  buildSurveySubmitBodyFromStorage,
  submitCustomerSurveyTng,
} from '@/api/customerSurvey';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import {
  getQ7_4Options,
  readQ7_4Answers,
  writeQ7_4Answers,
  type Q7_4Option,
} from '@/lib/q7_4Storage';
import { clearQ7_5Answers } from '@/lib/q7_5Storage';
import { clearQ7_6Answers } from '@/lib/q7_6Storage';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import { isOtherBrandsComplete } from '@/lib/otherBrandsStorage';
import {
  isSmokerProfileComplete,
  readSmokerProfileAnswers,
} from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import type { AppTranslations } from '@/locales';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

/** Match /7_1: bold white, body-size (not panel 20px). */
const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-base font-bold leading-snug text-white';

const checkboxClass =
  'mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-white/50 bg-black/20 accent-[#c9a882] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80';

/** Display label for a canonical (EN) /7_4 option. */
function q7_4OptionLabel(option: Q7_4Option, t: AppTranslations['q7_4']): string {
  switch (option) {
    case 'Taste/flavour':
      return t.optionTasteFlavour;
    case 'Overall experience':
      return t.optionOverallExperience;
    case 'Appearance/pack':
      return t.optionAppearancePack;
    case 'Something different from my usual choice':
      return t.optionSomethingDifferent;
    case 'Nothing in particular':
      return t.optionNothingInParticular;
    case 'Very likely':
      return t.optionVeryLikely;
    case 'Somewhat likely':
      return t.optionSomewhatLikely;
    case 'Not very likely':
      return t.optionNotVeryLikely;
    case 'Not at all likely':
      return t.optionNotAtAllLikely;
    case 'Not sure':
      return t.optionNotSure;
    default:
      return option;
  }
}

/**
 * LBS: like-most → `/7_5`.
 * Dice: mention likelihood + Submit → TnG (final Dice questionnaire step).
 */
export default function Q7_4() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const codeBrand = useAppSelector(selectCodeBrand);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const isDice = codeBrand === 'Dice';
  const options = useMemo(() => getQ7_4Options(codeBrand), [codeBrand]);
  const [selected, setSelected] = useState<Q7_4Option | null>(
    () => readQ7_4Answers()?.selected ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (isDice) return;
    if (!navigateAfterCheckRef.current) return;
    navigateAfterCheckRef.current = false;
    navigate('/7_5');
  }, [selected, navigate, isDice]);

  const selectOption = (label: Q7_4Option) => {
    if (isSubmitting) return;
    setWarning(null);
    if (isDice) {
      setSelected(label);
      writeQ7_4Answers({ selected: label });
      return;
    }
    setSelected((prev) => {
      if (prev === label) return prev;
      writeQ7_4Answers({ selected: label });
      clearQ7_5Answers();
      clearQ7_6Answers();
      navigateAfterCheckRef.current = true;
      return label;
    });
  };

  const handleDiceSubmit = () => {
    if (isSubmitting) return;
    setWarning(null);

    if (selected == null) {
      setWarning(t.q7_4.selectWarning);
      return;
    }
    if (!customerUuid) {
      setWarning(t.q7_4.sessionExpiredError);
      return;
    }
    const digits = (mobileLocalDigits ?? '').replace(/\D/g, '');
    if (digits.length === 0) {
      setWarning(t.q7_4.mobileError);
      return;
    }

    writeQ7_4Answers({ selected });

    const smoker = readSmokerProfileAnswers();
    const body = buildSurveySubmitBodyFromStorage({
      name: smoker?.name?.trim() || '',
      email: smoker?.email?.trim() || '',
      mobileLocalDigits: digits,
    });
    if (!body) {
      setWarning(t.q7_4.incompleteError);
      return;
    }

    setIsSubmitting(true);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.q7_4.saveError);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isOtherBrandsComplete()) {
    return <Navigate to="/otherbrands" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2Complete()) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden">
        {/* pt not mt: margin on flex-1 child is clipped by overflow-hidden (cuts bottom radius) */}
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[7vh] pb-4">
          {/* Panel fills viewport; answers scroll inside so bottom radius stays visible */}
          <SurveyBrownPanelInset className="flex min-h-0 flex-1 flex-col">
            <SurveyBrownPanel className="!flex !h-full !min-h-0 !flex-col !overflow-hidden !px-4 !pt-4 !pb-4">
              <p className="m-0 shrink-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                {isDice ? t.q7_4.diceQuestion : t.q7_4.question}
              </p>
              <div className="mt-[4.5rem] min-h-0 flex-1 overflow-y-auto sm:mt-[5.25rem]">
                <div className="flex flex-col gap-[1.875rem] pb-2 sm:gap-[2.25rem]">
                  {options.map((option) => {
                    const id = `q74-${option.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-']/g, '')}`;
                    const checked = selected === option;
                    return (
                      <label key={option} htmlFor={id} className={checkboxLabelClass}>
                        <input
                          id={id}
                          type="checkbox"
                          checked={checked}
                          disabled={isSubmitting}
                          onChange={() => selectOption(option)}
                          className={checkboxClass}
                        />
                        <span>{q7_4OptionLabel(option, t.q7_4)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {isDice ? (
                <>
                  {warning != null ? (
                    <div className="mt-4 shrink-0">
                      <SurveyValidationAlert>{warning}</SurveyValidationAlert>
                    </div>
                  ) : null}
                  <div className="mt-6 flex shrink-0 justify-center pb-1">
                    <MetallicPillButton
                      type="button"
                      size="sm"
                      className="metallic-pill--lang-home"
                      disabled={isSubmitting}
                      onClick={handleDiceSubmit}
                    >
                      {t.q7_4.submit}
                    </MetallicPillButton>
                  </div>
                </>
              ) : null}
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
